import { useState } from 'react';
import { Table, TableHeader, TableProvider, TableState, useTableState } from '@optimus-web/core';
import { TextKey } from '@mews-commander/enums';
import { LocalizedText, useSafeUrlState } from '@mews/framework';
import {
    EnterpriseBuilderQueueItemFilters,
    EnterpriseBuilderQueueItemsOverviewGetResult,
    QueueItemState,
    useRepositoryQuery,
} from '@mews-commander/data-access';
import { QueueItemQueryParams, queryParamsSchema } from '../../types';
import { getEnterpriseCopyTableColumns } from './tableMapping';
import { QueueItemDetailPanel } from './QueueItemDetailPanel';

export const QueueItemsTable = () => {
    const [tableState, setTableState] = useTableState<
        EnterpriseBuilderQueueItemsOverviewGetResult,
        EnterpriseBuilderQueueItemFilters
    >();
    const [urlParams, setUrlParams] = useSafeUrlState(queryParamsSchema);
    const [queueItemParams, setQueueItemParams] = useState<QueueItemQueryParams | undefined>({
        id: urlParams?.id,
        state: urlParams?.state,
    });
    const [opened, setOpened] = useState<boolean>(false);
    const { data, isFetching } = useEnterpriseCopyData(tableState);
    const totalRowCount = data?.totalRowCount || 0;

    const handleSidebarChange = (id: string, state: QueueItemState) => {
        setOpened(true);
        setQueueItemParams({ id, state });
        setUrlParams({ id, state });
    };

    return (
        <TableProvider
            state={tableState}
            onStateChange={setTableState}
            columns={getEnterpriseCopyTableColumns(handleSidebarChange)}
        >
            <TableHeader title={<LocalizedText textKey={TextKey.Properties} />} subTitle={totalRowCount} />
            <Table uniqueRowId="id" amountOfRows={totalRowCount} data={data?.data ?? []} loading={isFetching} />
            {queueItemParams?.id && queueItemParams?.state && !isFetching && (
                <QueueItemDetailPanel
                    onClose={() => {
                        setOpened(false);
                        setQueueItemParams(undefined);
                        setUrlParams({ id: undefined, state: undefined });
                    }}
                    opened={queueItemParams ? true : opened}
                    id={queueItemParams.id}
                    state={queueItemParams.state}
                    setQueueItemParams={setQueueItemParams}
                />
            )}
        </TableProvider>
    );
};

const useEnterpriseCopyData = (
    tableState: TableState<EnterpriseBuilderQueueItemsOverviewGetResult, EnterpriseBuilderQueueItemFilters>
) => useRepositoryQuery('enterpriseBuilder', 'getQueueItemsOverview', tableState, { refetchOnMount: 'always' });
